/**
 * Tâches : ce qu'on m'a demandé, ce que j'ai demandé (refonte v4 du 18/10/2026).
 *
 * Une tâche est une consigne courte, distincte d'une intervention. Trois
 * onglets à l'adresse (`onglet`) : les miennes, celles données à l'équipe, les
 * faites ; une recherche (`q`) ; la liste paginée par le serveur, sans coupure.
 * En tête des miennes, l'ajout rapide sur une ligne (exception documentée) ;
 * tout le reste passe par la page de création. « Fait » est le geste de chaque
 * ligne, le reste est dans son menu « … ».
 *
 * Un lien de notification (`?id=`) amène la tâche en tête de la liste, même
 * quand elle n'est pas dans l'onglet ouvert. Les anciennes adresses (`?tache=`,
 * `?vue=given`, `?nouvelle=1&debut=`) se lisent une fois, puis sont réécrites.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ListTodo, Plus } from 'lucide-react';
import { api } from '../../core/api.js';
import { useAuth } from '../../core/auth.js';
import { t } from '../../core/i18n.js';
import { EmptyState, Tabs } from '../../components/ui.js';
import { ListPage } from '../../components/templates/templates.js';
import { ListToolbar, LoadMore, useListState } from '../../components/ui/ListToolbar.js';
import { Banner, QueryBoundary } from '../../components/ui/Feedback.js';
import { QuickAdd } from '../../components/ui/QuickAdd.js';
import { Section } from '../../components/ui/Section.js';
import { useCursorList } from '../../components/ui/use-cursor-list.js';
import { RecordList } from '../../components/RecordRow.js';
import { TaskRow } from './TaskRow.js';
import {
  TASK_TABS, legacyTaskTarget, newTaskPath, tabQuery, type Task, type TaskCounts, type TaskTab,
} from './task-model.js';
import { useArrivedFeedback } from '../../components/ui/ReturnedFeedback.js';

export { newTaskPath };

interface TaskList extends TaskCounts { items: Task[] }

/** Tâches ouvertes de l'utilisateur, les plus urgentes en premier : partagé par
 *  les accueils (raccourci, carte). */
export function useOpenTasks(): { count: number; items: Task[]; loading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: ['tasks', 'mine', 'a_faire'],
    queryFn: () => api<TaskList>('/tasks?scope=mine&status=a_faire'),
    staleTime: 30_000,
  });
  return { count: data?.openCount ?? 0, items: data?.items ?? [], loading: isLoading };
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function TasksPage(): ReactNode {
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const [list, setList] = useListState({ q: '', onglet: 'mes' });
  const tab: TaskTab = (TASK_TABS as readonly string[]).includes(list.onglet) ? list.onglet as TaskTab : 'mes';
  const focusId = params.get('id') ?? '';
  const [feedback, setFeedback] = useArrivedFeedback();
  const [error, setError] = useState<string>();
  const [quickKey, setQuickKey] = useState<string>(() => crypto.randomUUID());
  const [quickPending, setQuickPending] = useState(false);
  const [quickError, setQuickError] = useState<string>();

  // Anciennes adresses : lues une fois, réécrites sous les noms communs.
  useEffect(() => {
    const target = legacyTaskTarget(params);
    if (target) navigate(target, { replace: true, state: location.state });
  }, [params]);

  const counts = useQuery({ queryKey: ['tasks', 'counts'], queryFn: () => api<TaskCounts>('/tasks/counts'), staleTime: 30_000 });
  const canAssign = counts.data?.canAssign ?? false;
  const tasks = useCursorList<Task>({
    queryKey: ['tasks', 'list'],
    path: '/tasks',
    filters: { ...tabQuery(tab), q: list.q.trim() || undefined },
  });
  const focus = useQuery({
    queryKey: ['tasks', 'one', focusId],
    queryFn: () => api<Task>(`/tasks/${focusId}`),
    enabled: UUID.test(focusId),
    retry: false,
  });
  const from = `/taches${params.toString() ? `?${params.toString()}` : ''}`;
  const refresh = (message?: string): void => {
    setError(undefined);
    if (message) setFeedback(message);
    void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    // Une tâche planifiée vit aussi dans le planning.
    void queryClient.invalidateQueries({ queryKey: ['planning'] });
  };
  const quickAdd = async (title: string): Promise<boolean> => {
    setQuickPending(true);
    setQuickError(undefined);
    try {
      await api('/tasks', { method: 'POST', body: { title, idempotencyKey: quickKey } });
      setQuickKey(crypto.randomUUID());
      refresh();
      setFeedback(t('taches.ajoutee'));
      return true;
    } catch (cause) {
      setQuickError((cause as Error).message);
      return false;
    } finally {
      setQuickPending(false);
    }
  };

  const filtering = Boolean(list.q.trim());
  const focused = focus.data && !(tasks.data ?? []).some((task) => task.id === focus.data?.id) ? focus.data : undefined;
  const empty = filtering ? t('taches.liste.aucunResultat')
    : tab === 'faites' ? t('taches.liste.aucuneFaite') : tab === 'equipe' ? t('taches.liste.aucuneEquipe') : t('taches.liste.rienAFaire');

  return (
    <ListPage
      header={{
        title: t('nav.taches'),
        description: t(`taches.liste.usage.${tab}`),
        ...(tab === 'equipe' && canAssign
          ? { primary: { label: t('taches.liste.donner'), icon: <Plus size={18} aria-hidden="true" />, to: '/taches/nouvelle', state: { from } } }
          : {}),
        tabs: (
          <Tabs<TaskTab>
            ariaLabel={t('taches.liste.ongletsAria')}
            value={tab}
            onChange={(onglet) => setList({ onglet })}
            options={[
              { value: 'mes', label: t('taches.liste.onglets.mes'), count: counts.data?.openCount },
              ...(canAssign ? [{ value: 'equipe' as const, label: t('taches.liste.onglets.equipe'), count: counts.data?.givenOpenCount }] : []),
              { value: 'faites', label: t('taches.liste.onglets.faites') },
            ]}
          />
        ),
      }}
      toolbar={(
        <ListToolbar
          search={{ value: list.q, onChange: (q) => setList({ q }), placeholder: t('taches.liste.rechercher'), label: t('taches.liste.rechercheLibelle') }}
          count={tasks.total !== undefined && filtering ? t('taches.liste.compte', { n: tasks.total }) : undefined}
        />
      )}
    >
      {tab === 'mes' ? (
        <QuickAdd
          label={t('taches.rapide.libelle')}
          placeholder={t('taches.rapide.exemple')}
          submitLabel={t('taches.rapide.ajouter')}
          maxLength={200}
          pending={quickPending}
          onSubmit={quickAdd}
          error={quickError}
          more={{ label: t('taches.rapide.plus'), to: '/taches/nouvelle', state: (title) => ({ from, title }) }}
        />
      ) : null}
      {feedback ? <Banner onDismiss={() => setFeedback(undefined)}>{feedback}</Banner> : null}
      {error ? <Banner tone="danger" onDismiss={() => setError(undefined)}>{error}</Banner> : null}
      {focus.error ? <Banner onDismiss={() => setParams((current) => { const next = new URLSearchParams(current); next.delete('id'); return next; }, { replace: true })}>{t('taches.liste.introuvable')}</Banner> : null}
      {focused ? (
        <Section title={t('taches.liste.signalee')} flush>
          <RecordList label={t('taches.liste.signalee')}>
            <li>
              <TaskRow task={focused} meId={me?.id} from={from} focused onChanged={refresh} onError={setError} />
            </li>
          </RecordList>
        </Section>
      ) : null}
      <QueryBoundary
        query={tasks}
        isEmpty={(rows) => rows.length === 0}
        empty={(
          <EmptyState>
            <ListTodo size={26} aria-hidden="true" />
            <span className="empty-title">{empty}</span>
            {tab === 'mes' && !filtering ? <span>{t('taches.liste.rienAFaireAide')}</span> : null}
          </EmptyState>
        )}
      >
        {(rows) => (
          <RecordList label={t(`taches.liste.onglets.${tab}`)}>
            {rows.map((task) => (
              <li key={task.id}>
                <TaskRow task={task} meId={me?.id} from={from} focused={task.id === focusId} onChanged={refresh} onError={setError} />
              </li>
            ))}
          </RecordList>
        )}
      </QueryBoundary>
      <LoadMore hasMore={tasks.hasMore} loading={tasks.loadingMore} failed={tasks.loadMoreFailed} onLoadMore={tasks.loadMore} />
    </ListPage>
  );
}
